import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { sessionsApi, blueprintsApi } from '../lib/api';

export default function Dashboard() {
  const { user } = useAuth();
  
  const { data: sessions } = useQuery({
    queryKey: ['sessions'],
    queryFn: sessionsApi.list,
  });

  const { data: blueprints } = useQuery({
    queryKey: ['blueprints'],
    queryFn: blueprintsApi.list,
  });

  const recentSessions = sessions?.slice(0, 5) || [];
  const inProgressSessions = sessions?.filter(s => s.status === 'in_progress') || [];

  return (
    <div className="space-y-6">
      {/* ウェルカムメッセージ */}
      <div className="bg-gradient-to-r from-navy-600 to-navy-500 rounded-xl p-6 border border-navy-400">
        <h1 className="text-2xl font-bold text-white mb-2">
          ようこそ、{user?.name}様
        </h1>
        <p className="text-gray-300">
          Jarvisがあなたのシステム開発をサポートします。
        </p>
      </div>

      {/* 統計カード */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-theme-card rounded-xl p-6 border border-theme transition-colors duration-300">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-blue-500 bg-opacity-20 flex items-center justify-center text-2xl">
              💬
            </div>
            <div>
              <p className="text-theme-secondary text-sm">セッション総数</p>
              <p className="text-2xl font-bold text-theme-primary">{sessions?.length || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-theme-card rounded-xl p-6 border border-theme transition-colors duration-300">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-yellow-500 bg-opacity-20 flex items-center justify-center text-2xl">
              ⏳
            </div>
            <div>
              <p className="text-theme-secondary text-sm">進行中</p>
              <p className="text-2xl font-bold text-theme-primary">{inProgressSessions.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-theme-card rounded-xl p-6 border border-theme transition-colors duration-300">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-green-500 bg-opacity-20 flex items-center justify-center text-2xl">
              📄
            </div>
            <div>
              <p className="text-theme-secondary text-sm">設計書</p>
              <p className="text-2xl font-bold text-theme-primary">{blueprints?.length || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* クイックアクション */}
      <div className="bg-theme-card rounded-xl p-6 border border-theme transition-colors duration-300">
        <h2 className="text-lg font-bold text-theme-primary mb-4">クイックアクション</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link
            to="/"
            className="flex items-center gap-4 p-4 bg-theme-hover rounded-lg hover:opacity-80 transition-all"
          >
            <div className="w-12 h-12 rounded-full bg-primary bg-opacity-20 flex items-center justify-center text-2xl">
              🤖
            </div>
            <div>
              <p className="font-medium text-theme-primary">新しい対話を始める</p>
              <p className="text-sm text-theme-secondary">Jarvisとシステム設計の相談</p>
            </div>
          </Link>
          
          <Link
            to="/sessions"
            className="flex items-center gap-4 p-4 bg-theme-hover rounded-lg hover:opacity-80 transition-all"
          >
            <div className="w-12 h-12 rounded-full bg-blue-500 bg-opacity-20 flex items-center justify-center text-2xl">
              📋
            </div>
            <div>
              <p className="font-medium text-theme-primary">セッション一覧</p>
              <p className="text-sm text-theme-secondary">過去の対話履歴を確認</p>
            </div>
          </Link>
        </div>
      </div>

      {/* 最近のセッション */}
      <div className="bg-theme-card rounded-xl p-6 border border-theme transition-colors duration-300">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-theme-primary">最近のセッション</h2>
          <Link to="/sessions" className="text-sm text-accent hover:underline">
            すべて見る →
          </Link>
        </div>
        
        {recentSessions.length > 0 ? (
          <div className="space-y-3">
            {recentSessions.map(session => (
              <Link
                key={session.id}
                to={`/sessions/${session.id}`}
                className="block p-4 bg-theme-hover rounded-lg hover:opacity-80 transition-all"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-theme-primary">
                      {session.initialIdea?.slice(0, 50) || '(アイデア未入力)'}
                      {(session.initialIdea?.length || 0) > 50 && '...'}
                    </p>
                    <p className="text-sm text-theme-secondary mt-1">
                      {new Date(session.createdAt).toLocaleDateString('ja-JP')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-xs ${
                      session.status === 'completed' ? 'bg-green-500 bg-opacity-20 text-green-500' :
                      session.status === 'in_progress' ? 'bg-yellow-500 bg-opacity-20 text-yellow-500' :
                      session.status === 'paused' ? 'bg-gray-500 bg-opacity-20 text-gray-500' :
                      'bg-red-500 bg-opacity-20 text-red-500'
                    }`}>
                      {session.status === 'completed' ? '完了' :
                       session.status === 'in_progress' ? '進行中' :
                       session.status === 'paused' ? '一時停止' : 'キャンセル'}
                    </span>
                    <span className="text-theme-secondary text-sm">
                      {session.answeredQuestions}/{session.totalQuestions}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-theme-secondary">
            <p>まだセッションがありません</p>
            <Link to="/" className="text-accent hover:underline mt-2 inline-block">
              Jarvisと対話を始める →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
